/**
 * AIChat · 通用对话组件
 *
 * 对应文档：docs/AI-Photo-Category/DEV_BRIEF.md §2.3、TECH_PLAN.md §4.1 / §4.2.1
 *
 * 提供：
 *  · 容器宽度观测 + sizeClass 派生
 *  · AIChatContext 下发
 *  · feed / composer 两段 flex 布局 · 分别装配 ChatFeed / Composer
 *  · 历史会话 overlay slot · 由 redux selectHistoryOverlayOpen 控制
 *  · "新对话"slide-up 动画 · 内层包装用 newConvAnimSeq 当 React key 触发重挂载
 *
 * 组件仅接受 className / style 两个 props，不接任何业务 props，
 * 调用方把它放进自己的布局容器（如 AISidebar 的面板体）即可。
 */

import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useAppSelector } from '../../../../app/hooks'
import { selectNewConvAnimSeq } from '../../../../slices/aiClassify'
import { trackAIChatClick } from '../../utils/trackClassify'
import HistoryConversationsOverlay from '../HistoryConversationsPopover'
import { useContainerWidth } from './useContainerWidth'
import { AIChatProvider } from './AIChatContext'
import ChatFeed from './components/ChatFeed'
import Composer from './components/Composer'
import styles from './AIChat.module.scss'

export interface AIChatProps {
  className?: string
  style?: React.CSSProperties
}

const AIChat: React.FC<AIChatProps> = ({ className, style }) => {
  // width 暂无消费方，但 Context 仍下发（sizeClass 依赖观测；width 预留）
  const { ref, width, sizeClass } = useContainerWidth<HTMLDivElement>()
  // "新对话"动画序列 · 每次 +1 → newConvLayer key 改变 → 内层重挂载播 slide-up
  const newConvSeq = useAppSelector(selectNewConvAnimSeq)

  useEffect(() => {
    trackAIChatClick('enterPage')
  }, [])
  /**
   * 记录"本 AIChat 实例挂载时 seq 的初始值"
   *  · 修 bad case：进文件夹详情页时 AIChat 全新挂载，但 redux 里的 newConvSeq
   *    可能是列表页留下的非零值 → 第一次渲染就带 newConvLayerAnimating 类 →
   *    CSS slide-up 立刻播一次，体感像是在"新建对话"
   *  · 修复：用 ref 锁住挂载瞬间的 seq；只有当前 seq 大于"挂载基线"才表示
   *    用户真的在本实例期间触发了新对话，那时才挂动画类
   *  · ref 不会因 key 改变而重置（key 重挂的是内层 div，AIChat 外壳没动）
   */
  const mountSeqRef = useRef(newConvSeq)
  const shouldAnimateNewConv = newConvSeq > mountSeqRef.current

  return (
    <AIChatProvider value={{ width, sizeClass }}>
      <div
        ref={ref}
        data-size-class={sizeClass}
        className={classNames(styles.root, className)}
        style={style}
      >
        {/* feed + composer 包在 newConvLayer 里 · key={newConvSeq} 让"新对话"
            动作触发整层 slide-up 重挂载，给用户"新对话从下方覆盖上来"的视觉
            seq=0（首次）也走一次入场，但 newConvLayerInit 用 0 时长动画跳过 */}
        <div
          key={newConvSeq}
          className={classNames(styles.newConvLayer, {
            [styles.newConvLayerAnimating]: shouldAnimateNewConv
          })}
        >
          <div className={styles.feed}>
            <ChatFeed />
          </div>
          <div className={styles.composer}>
            <Composer />
          </div>
        </div>
        {/* 历史会话 overlay · 顶层 absolute 覆盖 feed + composer，slide-up 入场
            自身根据 redux selectHistoryOverlayOpen 决定是否渲染 */}
        <HistoryConversationsOverlay />
      </div>
    </AIChatProvider>
  )
}

export default AIChat

// 辅助 API 供外部使用（测试 / Storybook / 直接消费 Context）
export { useContainerWidth, computeSizeClass } from './useContainerWidth'
export { useAIChat, AIChatProvider } from './AIChatContext'
export type { AIChatContextValue } from './AIChatContext'
